import React, { memo, useState, useMemo } from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import moment from 'moment';
import { useModalNotifications, useNotificationUtils } from '../hooks/useNotifications';
import { formatNotificationMessage } from '../services/notificationMessageUtils';

const NotificationRow = memo(({ notification, onMarkAsRead, onOpenTicket, isMarkingAsRead, index }) => {
  const { formatDate, formatTime } = useNotificationUtils();
  
  const handleMarkAsRead = () => {
    onMarkAsRead(notification);
  };

  const handleOpenTicket = () => {
    onOpenTicket(notification);
  };

  return (
    <tr>
      <td className="text-center">
        {notification.grupo_fisico_id || 'N/A'}
      </td>
      <td className="text-center font-weight-bold">
        {notification.numero_controlador || notification.controlador_id}
      </td>
      <td>
        {formatNotificationMessage(notification.mensagem)}
      </td>
      <td className="text-center">
        <div>
          {moment(notification.data_erro).format('DD/MM/YYYY')}
        </div>
        <small className="text-muted">
          {moment(notification.hora_erro, 'HH:mm:ss').format('HH:mm')}
        </small>
      </td>
      <td className="text-center">
        <div className="btn-group" role="group">
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Marcar como lida</Tooltip>}
          >
            <Button
              variant="success"
              size="sm"
              onClick={handleMarkAsRead}
              disabled={isMarkingAsRead}
              className="mr-1"
            >
              {isMarkingAsRead ? (
                <i className="fa fa-spinner fa-spin" />
              ) : (
                <i className="fa fa-check" />
              )}
            </Button>
          </OverlayTrigger>
          
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Abrir ticket de suporte</Tooltip>}
          >
            <Button
              variant="warning"
              size="sm"
              onClick={handleOpenTicket}
            >
              <i className="fa fa-ticket-alt" />
            </Button>
          </OverlayTrigger>
        </div>
      </td>
    </tr>
  );
});

const NotificationTable = memo(({ 
  notifications = [], 
  fromModal = false, 
  showActions = true,
  onTicketOpen 
}) => {
  const { markAsRead, isMarkingAsRead } = useModalNotifications();
  const [selectedNotifications, setSelectedNotifications] = useState(new Set());
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedNotificationForTicket, setSelectedNotificationForTicket] = useState(null);

  // Ordenar notificações por data (mais recentes primeiro)
  const sortedNotifications = useMemo(() => {
    return [...notifications].sort((a, b) => {
      const dateA = new Date(`${a.data_erro} ${a.hora_erro}`);
      const dateB = new Date(`${b.data_erro} ${b.hora_erro}`);
      return dateB - dateA;
    });
  }, [notifications]);

  const handleMarkAsRead = async (notification) => {
    try {
      await markAsRead(notification);
    } catch (error) {
      console.error('Erro ao marcar como lida:', error);
    }
  };

  const handleSelectAll = () => {
    if (selectedNotifications.size === sortedNotifications.length) {
      setSelectedNotifications(new Set());
    } else {
      setSelectedNotifications(new Set(sortedNotifications.map((_, index) => index)));
    }
  };

  const handleSelectNotification = (index) => {
    const newSelected = new Set(selectedNotifications);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedNotifications(newSelected);
  };

  const handleOpenTicket = (notification) => {
    setSelectedNotificationForTicket(notification);
    if (onTicketOpen) {
      onTicketOpen(notification);
    } else {
      setShowTicketModal(true);
    }
  };

  const handleCloseTicketModal = () => {
    setShowTicketModal(false);
    setSelectedNotificationForTicket(null);
  };

  if (sortedNotifications.length === 0) {
    return (
      <div className="text-center p-4">
        <i className="fa fa-inbox text-muted" style={{ fontSize: '48px' }} />
        <h4 className="mt-3 text-muted">Nenhuma notificação</h4>
      </div>
    );
  }

  return (
    <div className="notification-table-container" style={{ 
      background: 'white',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    }}>
      <style>{`
        .notification-table {
          margin-bottom: 0;
          border-collapse: separate;
          border-spacing: 0;
        }
        
        .notification-table thead th {
          background: #f8f9fa;
          border: none;
          border-bottom: 1px solid #dee2e6;
          font-weight: 600;
          font-size: 13px;
          color: #495057;
          padding: 16px 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          position: sticky;
          top: 0;
          z-index: 10;
        }
        
        .notification-table tbody tr {
          border-bottom: 1px solid #f1f3f4;
          transition: background-color 0.2s ease;
        }
        
        .notification-table tbody tr:nth-child(even) {
          background-color: #f8f9fa;
        }
        
        .notification-table tbody tr:nth-child(odd) {
          background-color: white;
        }
        
        .notification-table tbody tr:hover {
          background-color: #e3f2fd !important;
        }
        
        .notification-table tbody tr:last-child {
          border-bottom: none;
        }
        
        .notification-table tbody td {
          padding: 16px 12px;
          vertical-align: middle;
          font-size: 14px;
          border: none;
        }
        
        .notification-table .group-cell {
          font-weight: 600;
          color: #495057;
          text-align: center;
          min-width: 60px;
        }
        
        .notification-table .controller-cell {
          font-weight: 600;
          color: #495057;
          text-align: center;
          font-family: 'Consolas', 'Monaco', monospace;
          min-width: 80px;
        }
        
        .notification-table .message-cell {
          color: #495057;
          line-height: 1.4;
        }
        
        .notification-table .date-cell {
          color: #6c757d;
          text-align: center;
          font-size: 13px;
          min-width: 100px;
        }
        
        .notification-table .date-cell .date {
          font-weight: 500;
          color: #495057;
        }
        
        .notification-table .actions-cell {
          text-align: center;
          min-width: 100px;
        }
        
        .notification-action-btn {
          border: 1px solid #dee2e6;
          border-radius: 4px;
          padding: 8px 12px;
          margin: 0 2px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 36px;
          height: 32px;
          background: white;
          color: #495057;
        }
        
        .notification-action-btn:hover {
          border-color: #adb5bd;
          background: #f8f9fa;
          color: #212529;
        }
        
        .notification-action-btn.read-btn:hover {
          border-color: #28a745;
          color: #28a745;
        }
        
        .notification-action-btn.ticket-btn:hover {
          border-color: #ffc107;
          color: #856404;
        }
        
        .notification-action-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          background: #f8f9fa;
          border-color: #dee2e6;
          color: #6c757d;
        }
        
        @media (max-width: 768px) {
          .notification-table thead th,
          .notification-table tbody td {
            padding: 12px 8px;
            font-size: 12px;
          }
          
          .notification-action-btn {
            padding: 6px 8px;
            min-width: 30px;
            height: 28px;
          }
        }
      `}</style>

      <div className="table-responsive">
        <table className="table notification-table">
          <thead>
            <tr>
              <th style={{ width: '8%' }}>Grupo</th>
              <th style={{ width: '12%' }}>Controlador</th>
              <th style={{ width: '50%' }}>Mensagem</th>
              <th style={{ width: '15%' }}>Data/Hora</th>
              {showActions && <th style={{ width: '15%' }}>Ações</th>}
            </tr>
          </thead>
          <tbody>
            {sortedNotifications.map((notification, index) => (
              <tr key={`notification-${index}-${notification.controlador_id}-${notification.data_erro}`}>
                <td className="group-cell">
                  {notification.grupo_fisico_id || '-'}
                </td>
                <td className="controller-cell">
                  {notification.numero_controlador || notification.controlador_id}
                </td>
                <td className="message-cell">
                  <span title={formatNotificationMessage(notification.mensagem)}>
                    {formatNotificationMessage(notification.mensagem)}
                  </span>
                </td>
                <td className="date-cell">
                  <div className="date">
                    {moment(notification.data_erro).format('DD/MM/YYYY')}
                  </div>
                  <div>
                    {moment(notification.hora_erro, 'HH:mm:ss').format('HH:mm')}
                  </div>
                </td>
                {showActions && (
                  <td className="actions-cell">
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>Marcar como lida</Tooltip>}
                    >
                      <button
                        className="notification-action-btn read-btn"
                        onClick={() => handleMarkAsRead(notification)}
                        disabled={isMarkingAsRead}
                      >
                        {isMarkingAsRead ? (
                          <i className="fa fa-spinner fa-spin" />
                        ) : (
                          <i className="fa fa-check" />
                        )}
                      </button>
                    </OverlayTrigger>
                    
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>Abrir ticket</Tooltip>}
                    >
                      <button
                        className="notification-action-btn ticket-btn"
                        onClick={() => handleOpenTicket(notification)}
                      >
                        <i className="fa fa-ticket-alt" />
                      </button>
                    </OverlayTrigger>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Ticket Modal - opcional, pode ser passado via props */}
      {showTicketModal && selectedNotificationForTicket && (
        <div>
          {/* Aqui integraria com TicketSuporteForm se necessário */}
          {/* Por enquanto, só fecha o modal */}
          <button onClick={handleCloseTicketModal}>Fechar Ticket Modal</button>
        </div>
      )}
    </div>
  );
});

NotificationTable.displayName = 'NotificationTable';
NotificationRow.displayName = 'NotificationRow';

export default NotificationTable;